/**
 * Upload queue: drives many files through the two-phase S3 upload with
 * bounded concurrency.
 *
 * Per file:
 *   1. extract EXIF from the ORIGINAL (before any re-encode, so GPS survives)
 *   2. compress images over the threshold (JPEG)
 *   3. PUT the (possibly compressed) original to uploads/<event>/<ts>_<name>
 *   4. generate + PUT a thumbnail (image) or poster frame (video)
 *   5. PUT the EXIF sidecar JSON
 *
 * Fixes over the legacy uploader: stable per-task ids (not array indices),
 * p-limit concurrency (not a batch that blocks on its slowest file), unified
 * retry that reuses the same code path, res.ok checks, and consistent key
 * extensions for compressed output.
 */
import { useCallback, useRef, useState } from 'react';
import pLimit from 'p-limit';
import { getUploadUrl, putToS3 } from '../lib/apiClient.ts';
import { buildUploadKeys } from '../lib/keys.ts';
import { extractExif } from '../lib/exif.ts';
import { compressImage, generateThumbnail, generateVideoPoster } from '../lib/imageProcessing.ts';
import { isImageFile, kindFromFile } from '../lib/media.ts';
import { UPLOAD_CONFIG } from '../config/awsConfig.ts';
import type { ExifData } from '../lib/types.ts';

export type UploadStatus =
  | 'pending'
  | 'reading'
  | 'compressing'
  | 'uploading'
  | 'thumbnail'
  | 'done'
  | 'error';

export interface UploadTask {
  id: string;
  file: File;
  event: string;
  status: UploadStatus;
  progress: number; // 0-100
  error?: string;
  /** Final original key once uploaded. */
  originalKey?: string;
}

let taskSeq = 0;
function nextId(): string {
  return `task-${++taskSeq}`;
}

/** Adjust the filename extension to match a compressed JPEG output. */
function jpegName(name: string): string {
  return name.replace(/\.[^./\\]+$/, '') + '.jpg';
}

export interface UseUploadQueue {
  tasks: UploadTask[];
  uploading: boolean;
  enqueue: (files: File[], event: string) => void;
  start: () => Promise<void>;
  retry: (id: string) => Promise<void>;
  remove: (id: string) => void;
  clear: () => void;
}

export function useUploadQueue(): UseUploadQueue {
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const [uploading, setUploading] = useState(false);
  // Keep a ref mirror so async callbacks read current files without stale closures.
  const tasksRef = useRef<UploadTask[]>([]);
  tasksRef.current = tasks;

  const patch = useCallback((id: string, changes: Partial<UploadTask>) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...changes } : t)));
  }, []);

  const enqueue = useCallback((files: File[], event: string) => {
    const added = files.map<UploadTask>((file) => ({
      id: nextId(),
      file,
      event,
      status: 'pending',
      progress: 0,
    }));
    setTasks((prev) => [...prev, ...added]);
  }, []);

  const uploadOne = useCallback(
    async (task: UploadTask): Promise<void> => {
      const { file, event } = task;
      const timestamp = Date.now();

      // 1. EXIF from the original (images only) — before any re-encode.
      patch(task.id, { status: 'reading', progress: 5 });
      let exif: ExifData = { v: 1 };
      if (isImageFile(file)) {
        exif = await extractExif(file);
      }

      // 2. Compress large images.
      patch(task.id, { status: 'compressing', progress: 15 });
      const kind = kindFromFile(file);
      let body: Blob = file;
      let contentType = file.type || 'application/octet-stream';
      let uploadFileName = file.name;
      if (kind === 'image') {
        const processed = await compressImage(file);
        body = processed.blob;
        contentType = processed.type;
        if (processed.type === 'image/jpeg' && processed.blob !== file) {
          uploadFileName = jpegName(file.name);
        }
        if (processed.width && !exif.width) {
          exif.width = processed.width;
          exif.height = processed.height;
        }
      }

      const keys = buildUploadKeys(event, uploadFileName, timestamp);

      // 3. PUT the original.
      patch(task.id, { status: 'uploading', progress: 30, originalKey: keys.originalKey });
      const originalUrl = await getUploadUrl(keys.originalKey, contentType);
      await putToS3(originalUrl, body, contentType);

      // 4. Thumbnail (image) or poster (video).
      patch(task.id, { status: 'thumbnail', progress: 65 });
      let thumb: Blob | null = null;
      if (kind === 'image') {
        thumb = await generateThumbnail(file);
      } else if (kind === 'video') {
        thumb = await generateVideoPoster(file);
      }
      if (thumb) {
        const thumbUrl = await getUploadUrl(keys.thumbnailKey, 'image/jpeg');
        await putToS3(thumbUrl, thumb, 'image/jpeg');
      }

      // 5. Sidecar EXIF JSON (best-effort; failure doesn't fail the upload).
      patch(task.id, { progress: 90 });
      try {
        const sidecarBlob = new Blob([JSON.stringify(exif)], { type: 'application/json' });
        const sidecarUrl = await getUploadUrl(keys.sidecarKey, 'application/json');
        await putToS3(sidecarUrl, sidecarBlob, 'application/json');
      } catch {
        // Non-fatal: metadata can be backfilled later.
      }

      patch(task.id, { status: 'done', progress: 100 });
    },
    [patch],
  );

  const runPending = useCallback(
    async (ids: string[]): Promise<void> => {
      const limit = pLimit(UPLOAD_CONFIG.CONCURRENCY);
      setUploading(true);
      try {
        await Promise.all(
          ids.map((id) =>
            limit(async () => {
              const current = tasksRef.current.find((t) => t.id === id);
              if (!current) return;
              try {
                await uploadOne(current);
              } catch (err) {
                patch(id, {
                  status: 'error',
                  error: err instanceof Error ? err.message : String(err),
                });
              }
            }),
          ),
        );
      } finally {
        setUploading(false);
      }
    },
    [patch, uploadOne],
  );

  const start = useCallback(async () => {
    const ids = tasksRef.current
      .filter((t) => t.status === 'pending' || t.status === 'error')
      .map((t) => t.id);
    if (ids.length === 0) return;
    ids.forEach((id) => patch(id, { status: 'pending', progress: 0, error: undefined }));
    await runPending(ids);
  }, [patch, runPending]);

  const retry = useCallback(
    async (id: string) => {
      patch(id, { status: 'pending', progress: 0, error: undefined });
      await runPending([id]);
    },
    [patch, runPending],
  );

  const remove = useCallback((id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clear = useCallback(() => {
    setTasks((prev) => prev.filter((t) => t.status !== 'done'));
  }, []);

  return { tasks, uploading, enqueue, start, retry, remove, clear };
}
